import { useEffect, useRef } from "react";
import type { ArParkedUrls } from "./arDelivery";
import type { ModelViewerElement } from "./modelViewer";

// The hidden `<model-viewer>` AR launcher (ADR-0035 §3) — the ONE place that hands a
// parked GLB/USDZ to the OS AR engine, shared by BOTH AR entry points:
//
//   • `ArEntryButton` (#382) — the native-mobile button: tap → bake → park → mount
//      this → OS AR.
//   • the `/ar-launch` page (#383) — the desktop-QR handoff's far end: the phone
//      opens the hosted asset and taps → mount this → OS AR.
//
// It carries no bake and no network of its own: given the already-parked `https`
// URLs it mounts the launcher OFF-SCREEN, waits for the model to load, then activates
// AR — so the two callers own only their own state machine + copy, never a second
// copy of the model-viewer event dance. This is the mechanism spike #343 HITL-proved
// on real iOS Quick Look + Android Scene Viewer.

// If the launcher neither loads the parked model nor errors within this window, give
// up rather than leave the caller spinning forever — a stalled network is a failure
// too (ADR-0035 §7, "never shown broken"). `<model-viewer>` fires `error` on a bad
// URL; this catches a silent hang.
const AR_LAUNCH_TIMEOUT_MS = 20_000;

export interface ArModelViewerLauncherProps {
  // The parked delivery URLs to open in AR: `glbUrl` → `src` (Android WebXR / Scene
  // Viewer), `usdzUrl` → `ios-src` (iOS Quick Look). `src` (the GLB) is what
  // `<model-viewer>` actually loads to fire its `load` event on BOTH platforms, so a
  // parked GLB is expected even when the target is iOS (the bake parks both).
  urls: ArParkedUrls;
  // The OS now owns the AR session (it prompts for the camera itself). Fired once,
  // after `activateAR()` resolves — the caller returns to idle and unmounts this.
  onLaunched: () => void;
  // Load / activate / timeout failure — the caller shows its retry affordance.
  onError: () => void;
}

// The launcher is loaded LAZILY by each caller (`arLaunch.loadModelViewer`) BEFORE it
// mounts this, so the self-contained `<model-viewer>` dist (which carries its own
// three.js) never enters the main bundle. By mount time the custom element is defined
// and upgrades in place; this component only drives its lifecycle.
export default function ArModelViewerLauncher({
  urls,
  onLaunched,
  onError,
}: ArModelViewerLauncherProps) {
  const modelViewerRef = useRef<ModelViewerElement | null>(null);

  // Once the launcher finishes loading the model, hand the session to the OS AR
  // engine. `<model-viewer>` events are custom, so bind them imperatively on the
  // element (reliable across React's synthetic layer).
  useEffect(() => {
    const el = modelViewerRef.current;
    if (!el) return;

    let active = true;
    let timer = 0;

    const onLoad = () => {
      window.clearTimeout(timer);
      el.activateAR()
        .then(() => {
          if (active) onLaunched();
        })
        .catch(() => {
          if (active) onError();
        });
    };
    const onLoadError = () => {
      window.clearTimeout(timer);
      if (active) onError();
    };

    timer = window.setTimeout(() => {
      if (active) onError();
    }, AR_LAUNCH_TIMEOUT_MS);
    el.addEventListener("load", onLoad);
    el.addEventListener("error", onLoadError);

    return () => {
      active = false;
      window.clearTimeout(timer);
      el.removeEventListener("load", onLoad);
      el.removeEventListener("error", onLoadError);
    };
  }, [onLaunched, onError]);

  // Rendered OFF-SCREEN (not `display:none` — it needs a GL context to load the model
  // for the AR handoff), `loading="eager"` so it loads even off-viewport, inert +
  // hidden from assistive tech. The OS owns the camera prompt + surface coaching;
  // `tabIndex={-1}` + `aria-hidden` keep model-viewer's internal shadow-DOM AR
  // button/progress UI out of the tab order and the a11y tree, since we drive AR
  // programmatically (`activateAR`), not via that hidden control.
  return (
    <model-viewer
      ref={modelViewerRef}
      src={urls.glbUrl}
      ios-src={urls.usdzUrl}
      ar
      ar-modes="webxr scene-viewer quick-look"
      ar-scale="fixed"
      loading="eager"
      tabIndex={-1}
      aria-hidden="true"
      data-testid="ar-model-viewer"
      className="pointer-events-none fixed top-0 left-[-9999px] h-px w-px opacity-0"
    />
  );
}
